import ResearchAnswerQuestions from './components/ResearchAnswerQuestions';

import React, { useState } from 'react';
import DebugDragAndDropList from './components/DebugDragAndDropList';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import DoctorDashboard from './pages/DoctorDashboard';
import MedicalAdminDashboard from './pages/MedicalAdminDashboard';
import ProgramAdminDashboard from './pages/ProgramAdminDashboard';
import StatisticianDashboard from './pages/StatisticianDashboard';

// Doctor Pages
import DoctorProtocols from './pages/doctor/DoctorProtocols';
import DoctorCases from './pages/doctor/DoctorCases';
import DoctorBatches from './pages/doctor/DoctorBatches';
import DoctorAnswerQuestions from './pages/doctor/DoctorAnswerQuestions';
import DoctorProgressReport from './pages/doctor/DoctorProgressReport';
import ReviewCases from './pages/doctor/ReviewCases';
import DoctorContinueAnswer from './components/DoctorContinueAnswer';

// Medical Admin Pages
import MedicalAdminProtocols from './pages/medical-admin/MedicalAdminProtocols';
import MedicalAdminAssignCases from './pages/medical-admin/MedicalAdminAssignCases';
import MedicalAdminBatchCreation from './pages/medical-admin/MedicalAdminBatchCreation';
import MedicalAdminAssignCasesToBatch from './pages/medical-admin/MedicalAdminAssignCasesToBatch';
import MedicalAdminAssignBatchToDoctors from './pages/medical-admin/MedicalAdminAssignBatchToDoctors';
import MedicalAdminBatchSelection from './pages/medical-admin/MedicalAdminBatchSelection';
import MedicalAdminBatchSelectionForDoctors from './pages/medical-admin/MedicalAdminBatchSelectionForDoctors';
import MedicalAdminAssignProtocolsToBatch from './pages/medical-admin/MedicalAdminAssignProtocolsToBatch';
import MedicalViewReportsPage from './pages/medical-admin/ViewReportsPage';
import MedicalReportDetailPage from './pages/medical-admin/ReportDetailPage';
import CombinedReportsPage from './pages/medical-admin/CombinedReportsPage';
import TimeAnalyticsPage from './pages/medical-admin/TimeAnalyticsPage';
import BatchProtocolQuestionView from './pages/medical-admin/BatchProtocolQuestionView';
import QADataQualityPage from './pages/medical-admin/QADataQualityPage';
import DoctorBatchReport from './pages/medical-admin/DoctorBatchReport';

// Doctor Pages
import DoctorBatchCases from './pages/doctor/DoctorBatchCases';

// Program Admin Pages
import ProgramAdminCreateProtocol from './pages/program-admin/ProgramAdminCreateProtocol';
import ProgramAdminProtocols from './pages/program-admin/ProgramAdminProtocols';
import ProgramAdminQuestions from './pages/program-admin/ProgramAdminQuestions';
import ProgramAdminLinkQuestions from './pages/program-admin/ProgramAdminLinkQuestions';
import ProgramAdminAddDoctors from './pages/program-admin/ProgramAdminAddDoctors';
import ProgramAdminCases from './pages/program-admin/ProgramAdminCases';
import ProgramAdminEditProtocol from './pages/program-admin/ProgramAdminEditProtocol';
import ProgramAdminViewProtocol from './pages/program-admin/ProgramAdminViewProtocol';
import ProgramAdminAnswers from './pages/program-admin/ProgramAdminAnswers';
import ProgramAdminViewQuestions from './pages/program-admin/ProgramAdminViewQuestions';
import ProgramAdminEditQuestion from './pages/program-admin/ProgramAdminEditQuestion';
import ProgramAdminInactiveQuestions from './pages/program-admin/ProgramAdminInactiveQuestions';
import ProgramAdminReports from './pages/program-admin/ProgramAdminReports';
import QualityOfAnswersReport from './pages/program-admin/QualityOfAnswersReport';
import ProgramAdminTimeAnalyticsPage from './pages/program-admin/TimeAnalyticsPage';
import ProgramAdminStatisticalReportsPage from './pages/program-admin/StatisticalReportsPage';

// Statistician Pages
import AnalyticsPage from './pages/statistician/AnalyticsPage';
import ViewDataPage from './pages/statistician/ViewDataPage';
import CreateReportsPage from './pages/statistician/CreateReportsPage';
import ViewReportsPage from './pages/statistician/ViewReportsPage';
import ViewReportDetailPage from './pages/statistician/ViewReportDetailPage';
import EditReportPage from './pages/statistician/EditReportPage';

// Components
import MicrosoftCallback from './components/MicrosoftCallback';

// Styles
import './index.css';
import './assets/css/portal.css';
import './assets/css/Animate.css';

// Debug drag-and-drop test page
function DebugDragPage() {
  const [items, setItems] = useState([
    { id: 1, text: 'Item 1' },
    { id: 2, text: 'Item 2' },
    { id: 3, text: 'Item 3' },
  ]);
  return (
    <div style={{ maxWidth: 400, margin: '2rem auto' }}>
      <h2>Debug Drag and Drop</h2>
      <DebugDragAndDropList items={items} setItems={setItems} />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/auth/callback" element={<MicrosoftCallback />} />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/login" replace />} />

              {/* Doctor Routes */}
              <Route
                path="/doctor/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['doctor','Doctor','DOCTOR','doctor'.toLowerCase().replace(/\s+/g, '_')]}>
                    <DoctorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/protocols"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorProtocols />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/cases"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorCases />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/review-cases"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <ReviewCases />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/batches"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorBatchCases />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/batches-legacy"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorBatches />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/answer-questions"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorAnswerQuestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/answer-questions/start"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <ResearchAnswerQuestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/answer"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorContinueAnswer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/reports"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorProgressReport />
                  </ProtectedRoute>
                }
              />

              {/* Medical Admin Routes */}
              <Route
                path="/medical-admin/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/protocols"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminProtocols />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/assign-cases"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminAssignCases />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/batch"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminBatchCreation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/batch/assign-cases"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminBatchSelection />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/assign-cases-to-batch/:batchId"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminAssignCasesToBatch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/batch/assign-doctors"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminBatchSelectionForDoctors />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/assign-batch-to-doctors/:batchId"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminAssignBatchToDoctors />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/batch/assign-protocols"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminAssignProtocolsToBatch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/batch/assign-protocols-to-batch/:batchId"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalAdminAssignProtocolsToBatch />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/reports"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <CombinedReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/reports/:reportId"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <MedicalReportDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/time-analytics"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_'), 'program_admin', 'Program Admin', 'program admin', 'program_admin'.toLowerCase().replace(/\s+/g, '_'), 'statistician', 'Statistician', 'statistician', 'statistician'.toLowerCase().replace(/\s+/g, '_')]}>
                    <TimeAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/batch-time-tracking"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <BatchProtocolQuestionView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/quality-of-answers"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <QualityOfAnswersReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/qa-data-quality"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_'), 'program_admin','Program Admin','program admin','program_admin'.toLowerCase().replace(/\s+/g, '_'), 'statistician','Statistician','statistician','statistician'.toLowerCase().replace(/\s+/g, '_')]}>
                    <QADataQualityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/medical-admin/batch/:batchId/doctor/:doctorId/report"
                element={
                  <ProtectedRoute allowedRoles={['medical_admin','Medical Admin','medical admin','medical_admin'.toLowerCase().replace(/\s+/g, '_'), 'program_admin', 'Program Admin', 'program admin', 'program_admin'.toLowerCase().replace(/\s+/g, '_'), 'statistician', 'Statistician', 'statistician', 'statistician'.toLowerCase().replace(/\s+/g, '_')]}>
                    <DoctorBatchReport />
                  </ProtectedRoute>
                }
              />

              {/* Program Admin Routes */}
              <Route
                path="/program-admin/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['program_admin','Program Admin','program admin','program_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <ProgramAdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/create-protocol"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminCreateProtocol />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/questions"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminQuestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/link-questions"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminLinkQuestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/add-doctors"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminAddDoctors />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/cases"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminCases />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/edit-protocol/:id"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminEditProtocol />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/view-protocol/:id"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminViewProtocol />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/view-answers"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminAnswers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/reports"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/quality-of-answers"
                element={
                  <ProtectedRoute allowedRoles={['program_admin','Program Admin','program admin','program_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <QualityOfAnswersReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/qa-data-quality"
                element={
                  <ProtectedRoute allowedRoles={['program_admin','Program Admin','program admin','program_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <QADataQualityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/time-analytics"
                element={
                  <ProtectedRoute allowedRoles={['program_admin','Program Admin','program admin','program_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <ProgramAdminTimeAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/statistical-reports"
                element={
                  <ProtectedRoute allowedRoles={['program_admin','Program Admin','program admin','program_admin'.toLowerCase().replace(/\s+/g, '_')]}>
                    <ProgramAdminStatisticalReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/protocols"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminProtocols />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/questions/view-all"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminViewQuestions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/edit-question/:id"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminEditQuestion />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/program-admin/questions/inactive"
                element={
                  <ProtectedRoute allowedRoles={['program_admin']}>
                    <ProgramAdminInactiveQuestions />
                  </ProtectedRoute>
                }
              />

              {/* Statistician Routes */}
              <Route
                path="/statistician/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['statistician','Statistician','STATISTICIAN','statistician'.toLowerCase().replace(/\s+/g, '_')]}>
                    <StatisticianDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistician/analytics"
                element={
                  <ProtectedRoute allowedRoles={['statistician']}>
                    <AnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistician/data"
                element={
                  <ProtectedRoute allowedRoles={['statistician']}>
                    <ViewDataPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistician/reports"
                element={
                  <ProtectedRoute allowedRoles={['statistician']}>
                    <CreateReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistician/reports/view"
                element={
                  <ProtectedRoute allowedRoles={['statistician']}>
                    <ViewReportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistician/reports/:id"
                element={
                  <ProtectedRoute allowedRoles={['statistician']}>
                    <ViewReportDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/statistician/reports/edit/:id"
                element={
                  <ProtectedRoute allowedRoles={['statistician']}>
                    <EditReportPage />
                  </ProtectedRoute>
                }
              />

              {/* Debug drag-and-drop route */}
              <Route path="/debug-dnd" element={<DebugDragPage />} />
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
